import {Injectable} from '@angular/core';
import { AuthType } from "../models/auth-type.model";
import { CookieManagerService } from "./cookie-manager.service";

@Injectable({
  providedIn: 'root'
})
export class WebAuthnService {

  constructor(
    private cookieManagerService: CookieManagerService,
  ) {}

  /**
   * Check if the biometrics login UUID exists in the cookies.
   */
  get biometricsLoginUUIDExists() {
    return !!this.cookieManagerService.getLoginUUIDForAuthType(AuthType.BIOMETRICS);
  }

  /**
   * Encodes an ArrayBuffer to a Base64URL string.
   * Converts standard Base64 characters "+" and "/" to the URL-safe "-" and "_" respectively.
   * @param {ArrayBuffer} buffer - The buffer to encode.
   * @return {string} - The Base64URL-encoded string.
   */
  public encodeBase64Url(buffer: ArrayBuffer): string {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  /**
   * Decodes a Base64URL string into a Uint8Array.
   * Converts URL-safe Base64 characters "-" and "_" back to "+" and "/".
   * @param {string} value - The string to decode.
   * @return {Uint8Array} - The decoded array.
   */
  public decodeBase64Url(value: string): Uint8Array {
    const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
    const binaryString = atob(normalizedValue);
    return Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
  }

  /**
   * Transforms and safely serializes a `PublicKeyCredential` object into a format
   * suitable for server submission during WebAuthn registration or login.
   * @param {PublicKeyCredential} credential - Credential to serialize.
   * @return {any} - Serialized credential object.
   */
  public serializeCredential(credential: PublicKeyCredential): any {
    const response = credential.response;
    const isAttestationResponse =
      (response as AuthenticatorAttestationResponse).attestationObject !== undefined;

    if (isAttestationResponse) {
      // Registration scenario
      const attestationResponse = response as AuthenticatorAttestationResponse;
      return {
        id: credential.id,
        type: credential.type,
        rawId: this.encodeBase64Url(credential.rawId),
        response: {
          attestationObject: this.encodeBase64Url(attestationResponse.attestationObject),
          clientDataJSON: this.encodeBase64Url(attestationResponse.clientDataJSON),
        },
        clientExtensionResults: credential.getClientExtensionResults(),
      };
    } else {
      // Authentication scenario
      const assertionResponse = response as AuthenticatorAssertionResponse;
      return {
        id: credential.id,
        type: credential.type,
        rawId: this.encodeBase64Url(credential.rawId),
        response: {
          authenticatorData: this.encodeBase64Url(assertionResponse.authenticatorData),
          clientDataJSON: this.encodeBase64Url(assertionResponse.clientDataJSON),
          signature: this.encodeBase64Url(assertionResponse.signature),
          userHandle:
            assertionResponse.userHandle
              ? this.encodeBase64Url(assertionResponse.userHandle)
              : null,
        },
        clientExtensionResults: credential.getClientExtensionResults(),
      };
    }
  }

  /**
   * Decodes WebAuthn credential options (e.g., challenge, allowCredentials) for registration or authentication.
   * @param {string} request - WebAuthn request payload in JSON string format.
   * @return {any} - Decoded credential options.
   */
  public decodeCredentialOptions(request: string): any {
    const options = JSON.parse(request);

    if (options?.publicKey?.challenge) {
      options.publicKey.challenge = this.decodeBase64Url(options.publicKey.challenge);
    }
    if (options?.publicKey?.allowCredentials) {
      options.publicKey.allowCredentials = options.publicKey.allowCredentials.map((credential: any) => ({
        ...credential,
        id: this.decodeBase64Url(credential.id),
      }));
    }
    if (options?.publicKey?.user?.id) {
      options.publicKey.user.id = this.decodeBase64Url(options.publicKey.user.id);
    }

    return options;
  }

}
